import SocketService, { NAMESPACES } from '../SocketService';

export const OnlineStatus = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  AWAY: 'away',
  BUSY: 'busy',
};

class UserStatusSocketService {
  constructor() {
    this.socketService = new SocketService(NAMESPACES.USER);
    this.statusListeners = new Set();
    this.onlineUsers = new Set();
    this.userStatuses = new Map(); // userId -> status
    this.isInitialized = false;
    this.listenersSetup = false;
    this.cleanupFns = [];
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.socketService.connect();
      this.setupListeners();
      
      // Start sending heartbeats
      this.startHeartbeat();
      
      this.isInitialized = true;
      console.log('✅ UserStatusSocketService initialized');
    } catch (error) {
      console.error('Failed to initialize UserStatusSocketService:', error);
      throw error;
    }
  }

  setupListeners() {
    console.log('🔧 UserStatusSocketService: Setting up listeners');
    
    // Don't setup if already setup
    if (this.listenersSetup) {
      console.log('ℹ️ Listeners already setup, skipping...');
      return;
    }
    
    // Listen for user status changes
    const cleanupStatusChanged = this.socketService.on('userStatusChanged', (data) => {
      console.log('📥 UserStatusSocketService: Received userStatusChanged event:', data);
      const { userId, status, isOnline } = data;
      
      if (isOnline) {
        this.onlineUsers.add(userId);
      } else {
        this.onlineUsers.delete(userId);
      }
      
      this.userStatuses.set(userId, status);
      
      // Notify listeners
      this.notifyListeners({
        type: 'statusChanged',
        userId,
        status,
        isOnline
      });
    });
    this.cleanupFns.push(cleanupStatusChanged);

    // Listen for legacy user:status events too
    const cleanupUserStatus = this.socketService.on('user:status', (data) => {
      console.log('📥 UserStatusSocketService: Received user:status event:', data);
      const { userId, status, isOnline } = data;
      
      if (isOnline) {
        this.onlineUsers.add(userId);
      } else {
        this.onlineUsers.delete(userId);
      }
      
      this.userStatuses.set(userId, status);
      
      this.notifyListeners({
        type: 'statusChanged',
        userId,
        status,
        isOnline
      });
    });
    this.cleanupFns.push(cleanupUserStatus);

    // Listen for initial online users list
    const cleanupOnlineUsers = this.socketService.on('onlineUsers', (data) => {
      console.log('📥 UserStatusSocketService: Received onlineUsers event:', data);
      const { onlineUsers } = data;
      
      // Reset sets
      this.onlineUsers.clear();
      this.userStatuses.clear();
      
      // Update with new data
      onlineUsers.forEach(user => {
        this.onlineUsers.add(user.userId);
        this.userStatuses.set(user.userId, user.status);
      });
      
      // Notify listeners
      this.notifyListeners({
        type: 'onlineUsersUpdated',
        onlineUsers
      });
    });
    this.cleanupFns.push(cleanupOnlineUsers);

    // Listen for status change confirmation
    const cleanupStatusConfirm = this.socketService.on('statusChanged', (data) => {
      this.notifyListeners(data);
    });
    this.cleanupFns.push(cleanupStatusConfirm);
    
    // Listen for disconnect
    const cleanupDisconnect = this.socketService.on('disconnect', (reason) => {
      console.log('UserStatusSocket disconnected:', reason);
    });
    this.cleanupFns.push(cleanupDisconnect);
    
    // Listen for reconnect
    const cleanupReconnect = this.socketService.on('reconnect', (attemptNumber) => {
      console.log(`UserStatusSocket reconnected after ${attemptNumber} attempts`);
      // Request updated online users list
      this.socketService.emit('getOnlineUsers');
    });
    this.cleanupFns.push(cleanupReconnect);
    
    this.listenersSetup = true;
    console.log('✅ UserStatusSocketService listeners setup complete');
  }

  /**
   * Change the current user's status
   * @param {string} status - One of OnlineStatus values
   */
  changeStatus(status) {
    if (!Object.values(OnlineStatus).includes(status)) {
      throw new Error(`Invalid status: ${status}`);
    }

    if (!this.socketService.isConnected) {
      console.warn('Socket not connected, status change will be queued');
    }

    this.socketService.emit('changeStatus', { status });
    console.log(`✅ Status change requested: ${status}`);
  }

  /**
   * Send heartbeat to update last seen
   */
  sendHeartbeat() {
    if (!this.socketService.isConnected) {
      return;
    }
    this.socketService.emit('heartbeat');
  }

  /**
   * Check if a user is online
   * @param {string} userId
   * @returns {boolean}
   */
  isUserOnline(userId) {
    return this.onlineUsers.has(userId);
  }

  /**
   * Get a user's current status
   * @param {string} userId
   * @returns {string} Status or 'offline' if not found
   */
  getUserStatus(userId) {
    return this.userStatuses.get(userId) || OnlineStatus.OFFLINE;
  }

  /**
   * Get all online users
   * @returns {Array<string>} Array of user IDs
   */
  getOnlineUsers() {
    return Array.from(this.onlineUsers);
  }

  /**
   * Subscribe to status changes
   * @param {Function} callback - Called when any user's status changes
   * @returns {Function} Unsubscribe function
   */
  onStatusChange(callback) {
    this.statusListeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  /**
   * Notify all listeners about a status change
   * @private
   */
  notifyListeners(data) {
    this.statusListeners.forEach(listener => {
      try {
        listener(data);
      } catch (error) {
        console.error('Error in status listener:', error);
      }
    });
  }

  /**
   * Disconnect from the socket
   */
  async disconnect() {
    console.log('🔌 Disconnecting UserStatusSocketService...');
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Clean up listeners
    this.cleanupFns.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error cleaning up listener:', error);
      }
    });
    this.cleanupFns = [];
    
    await this.socketService.disconnect();
    this.isInitialized = false;
    this.listenersSetup = false;
  }

  /**
   * Start sending periodic heartbeats
   * @param {number} interval - Interval in milliseconds (default: 30 seconds)
   */
  startHeartbeat(interval = 30000) {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, interval);
  }

  /**
   * Stop sending heartbeats
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

// Export singleton instance
const userStatusSocketService = new UserStatusSocketService();
export default userStatusSocketService;